/**
 * Fusion — drawer
 *
 * Base off-canvas panel used by the mobile menu, cart drawer and search.
 * Handles focus trapping, scroll locking, escape/overlay dismissal and the
 * aria-expanded state of whatever triggered it.
 *
 * Other components extend this rather than reimplementing modal behaviour:
 *   class FusionCartDrawer extends FusionDrawer { ... }
 */

class FusionDrawer extends HTMLElement {
  connectedCallback() {
    this.releaseFocus = null;
    this.trigger = null;

    this.onKeydown = this.onKeydown.bind(this);

    this.bindPanel();

    // Any control anywhere on the page can open this drawer by id
    document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((control) => {
      control.addEventListener('click', (event) => {
        // Cart and account links are real hrefs — only hijack them when JS is driving
        event.preventDefault();
        this.trigger = control;
        this.toggle();
      });
    });
  }

  /**
   * Caches panel references and binds dismissal controls.
   *
   * Kept separate from connectedCallback so subclasses that replace their own
   * innerHTML (the cart drawer re-renders after every mutation) can rebind
   * without re-running the trigger wiring and double-binding it.
   */
  bindPanel() {
    this.panel = this.querySelector('[data-drawer-panel]');
    this.overlay = this.querySelector('[data-drawer-overlay]');

    this.querySelectorAll('[data-drawer-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    this.overlay?.addEventListener('click', () => this.close());
  }

  disconnectedCallback() {
    // Guard against a section reload leaving the page scroll-locked
    if (this.open) this.close();
  }

  get open() {
    return !this.hasAttribute('hidden');
  }

  toggle() {
    this.open ? this.close() : this.show();
  }

  show() {
    if (this.open) return;

    this.removeAttribute('hidden');

    // Forces a style recalc so the transition runs from the hidden state
    void this.offsetWidth;

    this.classList.add('is-open');
    this.overlay?.classList.add('is-active');
    this.trigger?.setAttribute('aria-expanded', 'true');

    window.Fusion.lockScroll();
    this.releaseFocus = window.Fusion.trapFocus(this.panel, this.initialFocusTarget());

    document.addEventListener('keydown', this.onKeydown);
    this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
  }

  close() {
    if (!this.open) return;

    this.classList.remove('is-open');
    this.overlay?.classList.remove('is-active');
    this.trigger?.setAttribute('aria-expanded', 'false');

    document.removeEventListener('keydown', this.onKeydown);

    this.releaseFocus?.();
    this.releaseFocus = null;

    window.Fusion.unlockScroll();

    // Wait for the panel transition before hiding, so it animates out
    const done = () => {
      this.setAttribute('hidden', '');
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    };

    if (window.Fusion.motionAllowed()) {
      this.panel?.addEventListener('transitionend', done, { once: true });
      // Fallback in case the transition never fires (display:none, reduced motion)
      window.setTimeout(done, 400);
    } else {
      done();
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }

  /** Overridden by subclasses that want to focus something specific. */
  initialFocusTarget() {
    return null;
  }
}

if (!customElements.get('fusion-drawer')) {
  customElements.define('fusion-drawer', FusionDrawer);
}

window.FusionDrawer = FusionDrawer;
